import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import React from 'react';

import ButtonLink from '../links/ButtonLink';

const languages = [
  { name: 'Arabic', flag: '🇸🇦', slug: 'arabic' },
  { name: 'Bulgarian', flag: '🇧🇬', slug: 'bulgarian' },
  { name: 'Chinese', flag: '🇨🇳', slug: 'chinese' },
  { name: 'Croatian', flag: '🇭🇷', slug: 'croatian' },
  { name: 'Czech', flag: '🇨🇿', slug: 'czech' },
  { name: 'Danish', flag: '🇩🇰', slug: 'danish' },
  { name: 'Dutch', flag: '🇳🇱', slug: 'dutch' },
  { name: 'English', flag: '🇬🇧', slug: 'english' },
  { name: 'Estonian', flag: '🇪🇪', slug: 'estonian' },
  { name: 'Filipino', flag: '🇵🇭', slug: 'filipino' },
  { name: 'Finnish', flag: '🇫🇮', slug: 'finnish' },
  { name: 'French', flag: '🇫🇷', slug: 'french' },
  { name: 'German', flag: '🇩🇪', slug: 'german' },
  { name: 'Greek', flag: '🇬🇷', slug: 'greek' },
  { name: 'Hebrew', flag: '🇮🇱', slug: 'hebrew' },
  { name: 'Hindi', flag: '🇮🇳', slug: 'hindi' },
  { name: 'Hungarian', flag: '🇭🇺', slug: 'hungarian' },
  { name: 'Icelandic', flag: '🇮🇸', slug: 'icelandic' },
  { name: 'Indonesian', flag: '🇮🇩', slug: 'indonesian' },
  { name: 'Italian', flag: '🇮🇹', slug: 'italian' },
  { name: 'Japanese', flag: '🇯🇵', slug: 'japanese' },
  { name: 'Kazakh', flag: '🇰🇿', slug: 'kazakh' },
  { name: 'Korean', flag: '🇰🇷', slug: 'korean' },
  { name: 'Latvian', flag: '🇱🇻', slug: 'latvian' },
  { name: 'Lithuanian', flag: '🇱🇹', slug: 'lithuanian' },
  { name: 'Macedonian', flag: '🇲🇰', slug: 'macedonian' },
  { name: 'Malay', flag: '🇲🇾', slug: 'malay' },
  { name: 'Norwegian', flag: '🇳🇴', slug: 'norwegian' },
  { name: 'Polish', flag: '🇵🇱', slug: 'polish' },
  { name: 'Portuguese', flag: '🇵🇹', slug: 'portuguese' },
  { name: 'Romanian', flag: '🇷🇴', slug: 'romanian' },
  { name: 'Russian', flag: '🇷🇺', slug: 'russian' },
  { name: 'Serbian', flag: '🇷🇸', slug: 'serbian' },
  { name: 'Slovak', flag: '🇸🇰', slug: 'slovak' },
  { name: 'Slovenian', flag: '🇸🇮', slug: 'slovenian' },
  { name: 'Spanish', flag: '🇪🇸', slug: 'spanish' },
  { name: 'Swedish', flag: '🇸🇪', slug: 'swedish' },
  { name: 'Tamil', flag: '🇮🇳', slug: 'tamil' },
  { name: 'Thai', flag: '🇹🇭', slug: 'thai' },
  { name: 'Turkish', flag: '🇹🇷', slug: 'turkish' },
  { name: 'Ukrainian', flag: '🇺🇦', slug: 'ukrainian' },
  { name: 'Vietnamese', flag: '🇻🇳', slug: 'vietnamese' },
];

const TranscriptionLanguages = () => {
  const { t } = useLocale('ai-transcription');

  return (
    <div className='relative isolate bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm pb-24 pt-8'>
      {/* Subtle background glow */}
      <div
        className='absolute left-1/2 top-1/2 -z-10 -translate-x-1/2 -translate-y-1/2 transform-gpu blur-3xl opacity-20 dark:opacity-10 pointer-events-none'
        aria-hidden='true'
      >
        <div className='aspect-[1000/600] w-[60rem] bg-gradient-to-r from-primary-400 to-indigo-500 rounded-full' />
      </div>

      <div className='mx-auto max-w-7xl py-12 px-6 lg:grid lg:grid-cols-3 lg:gap-x-12 lg:px-8'>
        <div className='max-w-xl'>
          <div className='mb-6'>
            <div className='inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-sm font-bold text-indigo-600 ring-1 ring-inset ring-indigo-600/20 dark:bg-indigo-500/10 dark:text-indigo-400 dark:ring-indigo-500/20'>
              <svg
                className='w-4 h-4 mr-2'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  d='M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9'
                />
              </svg>
              {t('Global Reach')}
            </div>
          </div>
          <h2 className='text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-5xl lg:text-balance'>
            {t('Supported Languages')}
          </h2>
          <p className='mt-6 text-lg leading-8 text-gray-600 dark:text-slate-400'>
            {t(
              'Our AI transcription service supports a wide range of languages. Whether you need transcription services for meetings, lectures, podcasts, or everyday conversations, our AI has your back.',
            )}
          </p>
        </div>
        <div className='mt-16 lg:mt-0 lg:col-span-2'>
          <dl className='flex flex-wrap gap-3 sm:justify-start lg:justify-end'>
            {languages.map((language, index) => (
              <div key={index} className='relative group'>
                <Link href={`/ai-transcription/${language.slug}`}>
                  <dt className='flex items-center rounded-full bg-white/70 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-sm ring-1 ring-gray-900/5 backdrop-blur-md transition-all duration-300 hover:-translate-y-1 hover:shadow-md hover:ring-primary-500/30 hover:bg-primary-50 dark:bg-slate-800/60 dark:text-slate-200 dark:ring-white/10 dark:hover:bg-slate-800 dark:hover:text-white dark:hover:ring-primary-500/50 cursor-pointer'>
                    <span className='mr-2 text-xl drop-shadow-sm group-hover:scale-110 transition-transform duration-300'>
                      {language.flag}
                    </span>{' '}
                    <span>{t(language.name)}</span>
                  </dt>
                </Link>
              </div>
            ))}
          </dl>
        </div>
      </div>
      <div className='mt-12 sm:flex sm:justify-center px-6'>
        <ButtonLink
          href='/audios'
          variant='dark'
          className='group relative inline-flex items-center justify-center rounded-full bg-gray-900 px-10 py-4 text-base font-bold text-white shadow-lg hover:bg-gray-800 hover:-translate-y-1 hover:shadow-xl dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 transition-all duration-300'
        >
          {t('Try it yourself for free')}
          <svg
            className='ml-2 h-5 w-5 transition-transform group-hover:translate-x-1'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
            strokeWidth='2.5'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3'
            />
          </svg>
        </ButtonLink>
      </div>
    </div>
  );
};

export default TranscriptionLanguages;
